import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
} from '@makeably/creativex-design-system';
import {
  emptyState,
  filterProps,
  getDateTimeDimension,
  getExplorerLinkDimension,
  getLinkDimension,
  NULL_DIMENSION,
  NULL_LABEL,
  NULL_VALUE,
} from 'components/internal/shared';
import FilterTags from 'components/molecules/FilterTags';
import ItemsFilter from 'components/molecules/ItemsFilter';
import ItemsTable from 'components/molecules/ItemsTable';
import { saveFilterQuery } from 'utilities/filtering';
import {
  getItemSortBy,
  isDefined,
} from 'utilities/item';
import {
  filterItems,
  getValueOptions,
} from 'utilities/itemFilter';
import {
  internalPipelineTrackersPath,
  internalAdPath,
  internalFacebookAdCreativePath,
  internalFacebookAdGroupPath,
} from 'utilities/routes';
import {
  splitSnakeCase,
  titleize,
} from 'utilities/string';
import {
  getParamsObj,
  getPage,
  getParams,
  setParam,
} from 'utilities/url';

const adProps = PropTypes.shape({
  adDataAddedAt: PropTypes.string,
  assetAddedAt: PropTypes.string,
  assetId: PropTypes.number,
  auditableId: PropTypes.number.isRequired,
  auditableType: PropTypes.string.isRequired,
  auditObjectsAddedAt: PropTypes.string,
  auditPostId: PropTypes.number,
  channelName: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  cvProcessedAt: PropTypes.string,
  datedAuditPostsCreatedAt: PropTypes.string,
  id: PropTypes.number.isRequired,
  initialReviewAt: PropTypes.string,
  rulesEngineRunAt: PropTypes.string,
  status: PropTypes.string,
});

const propTypes = {
  ads: PropTypes.arrayOf(adProps).isRequired,
  date: PropTypes.string.isRequired,
  filterSelections: filterProps,
  step: PropTypes.string.isRequired,
};

const defaultProps = {
  filterSelections: {},
};

const filterDimensions = [
  {
    label: 'Auditable Type',
    value: 'auditableType',
  },
  {
    label: 'Bucket',
    value: 'bucket',
  },
  {
    label: 'Channel Name',
    value: 'channelName',
  },
  {
    label: 'Status',
    value: 'status',
  },
];

const headers = [
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'auditableType',
    label: 'Auditable Type',
  },
  {
    key: 'channelName',
    label: 'Channel Name',
  },
  {
    key: 'bucket',
    label: 'Bucket',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'auditableId',
    label: 'Auditable ID',
  },
  {
    key: 'assetId',
    label: 'Asset ID',
  },
  {
    key: 'auditPostId',
    label: 'Audit Post ID',
  },
  {
    key: 'createdAt',
    label: 'Created',
  },
  {
    key: 'adDataAddedAt',
    label: 'Ad Data Added',
  },
  {
    key: 'assetAddedAt',
    label: 'Asset Added',
  },
  {
    key: 'cvProcessedAt',
    label: 'CV Processed',
  },
  {
    key: 'auditObjectsAddedAt',
    label: 'Audit Objects Added',
  },
  {
    key: 'rulesEngineRunAt',
    label: 'Rules Engine Run',
  },
  {
    key: 'initialReviewAt',
    label: 'Initial Review',
  },
  {
    key: 'datedAuditPostsCreatedAt',
    label: 'Dated Audit Posts Created',
  },
];

function getPathParams(params) {
  const obj = getParamsObj(params, ['date', 'end_date', 'step']);

  return {
    ...obj,
    end_date: obj.end_date ?? obj.date,
  };
}

function getBucketPath(auditableType, auditableId) {
  switch (auditableType) {
    case 'Ad':
      return internalAdPath(auditableId);
    case 'FacebookAdCreative':
      return internalFacebookAdCreativePath(auditableId);
    case 'FacebookAdGroup':
      return internalFacebookAdGroupPath(auditableId);
    default:
      return null;
  }
}

function getBucketDimension(bucket, auditableType, auditableId) {
  if (!isDefined(bucket)) return NULL_DIMENSION;

  const value = titleize(splitSnakeCase(bucket));
  const url = getBucketPath(auditableType, auditableId);

  return getLinkDimension(value, url, true);
}

function getItems(ads) {
  return ads.map(({
    adDataAddedAt,
    assetAddedAt,
    assetId,
    auditableType,
    auditableId,
    auditObjectsAddedAt,
    auditPostId,
    bucket,
    channelName,
    createdAt,
    cvProcessedAt,
    datedAuditPostsCreatedAt,
    id,
    initialReviewAt,
    rulesEngineRunAt,
    status,
  }) => ({
    adDataAddedAt: getDateTimeDimension(adDataAddedAt),
    assetAddedAt: getDateTimeDimension(assetAddedAt),
    assetId: getExplorerLinkDimension('Asset', assetId, null, true),
    auditableId: getExplorerLinkDimension(auditableType, auditableId, null, true),
    auditableType: { value: auditableType },
    auditObjectsAddedAt: getDateTimeDimension(auditObjectsAddedAt),
    auditPostId: getExplorerLinkDimension('AuditPost', auditPostId, null, true),
    bucket: getBucketDimension(bucket, auditableType, auditableId),
    channelName: { value: channelName },
    createdAt: getDateTimeDimension(createdAt),
    cvProcessedAt: getDateTimeDimension(cvProcessedAt),
    datedAuditPostsCreatedAt: getDateTimeDimension(datedAuditPostsCreatedAt),
    id: { value: id },
    initialReviewAt: getDateTimeDimension(initialReviewAt),
    rulesEngineRunAt: getDateTimeDimension(rulesEngineRunAt),
    status: {
      label: status ? titleize(status) : NULL_LABEL,
      value: status ?? NULL_VALUE,
    },
  }));
}

function PipelineTrackerAds({
  ads,
  date,
  filterSelections: initialFilterSelections,
  step,
}) {
  const params = getParams(window);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSelections, setFilterSelections] = useState(initialFilterSelections);
  const [sort, setSort] = useState({
    asc: true,
    key: 'id',
  });
  const [page, setPage] = useState(getPage(params));

  const items = useMemo(() => getItems(ads), [ads]);

  const filterOptions = useMemo(() => (
    getValueOptions(filterDimensions, items)
  ), [items]);

  const filteredItems = useMemo(() => (
    filterItems(items, filterSelections)
  ), [items, filterSelections]);

  const sortedItems = useMemo(() => (
    filteredItems.slice().sort(getItemSortBy(sort.key, sort.asc))
  ), [filteredItems, sort]);

  const handleFilterSelect = async (value) => {
    setPage(1);
    setFilterSelections(value);

    const uuid = Object.keys(value).length ? await saveFilterQuery(value) : '';
    setParam('filter_uuid', uuid, params, window);
  };

  return (
    <Card>
      <div className="u-flexRow u-gap-16 u-justifyBetween">
        <div>
          <h4 className="u-marginBottom-8">Pipeline Tracker Ads</h4>
          <div>
            <div className="u-flexRow u-gap-8">
              <div>Date:</div>
              <h6>{ date }</h6>
            </div>
            <div className="u-flexRow u-gap-8">
              <div>Step:</div>
              <h6>{ step }</h6>
            </div>
          </div>
        </div>
        <Button
          iconRight="arrowRight"
          label="Back to Tracker Section"
          url={internalPipelineTrackersPath(getPathParams(params))}
          variant="tertiary"
        />
      </div>
      <div className="u-flexRow u-alignCenter u-gap-8 u-marginTop-24">
        <ItemsFilter
          dimensions={filterDimensions}
          isOpen={filterOpen}
          options={filterOptions}
          selections={filterSelections}
          onClose={() => setFilterOpen(false)}
          onOpen={() => setFilterOpen(true)}
          onSelect={handleFilterSelect}
        />
        <FilterTags
          dimensions={filterDimensions}
          selections={filterSelections}
          onClick={() => setFilterOpen(true)}
          onRemove={handleFilterSelect}
        />
      </div>
      <ItemsTable
        className="u-scrollShadowRight"
        emptyTableContent={emptyState}
        headers={headers}
        items={sortedItems}
        page={page}
        sort={sort}
        onPageChange={setPage}
        onSortChange={setSort}
      />
    </Card>
  );
}

PipelineTrackerAds.propTypes = propTypes;
PipelineTrackerAds.defaultProps = defaultProps;

export default PipelineTrackerAds;
